import React, { useEffect } from 'react';
import { useStore } from '../../../../../../../stores/root';
import moment from 'moment';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../../../services/validation';
import GeneralService from '../../../../../../../services/general';

// props

// components
import { DatePicker, DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../../../../services/finance/incomes/invoices/invoice';
import ErrorService from '../../../../../../../services/general/error';
import PermissionsService from '../../../../../../../services/permissions';
import { IIncomeInvoicePartResourceShortProps } from '../../../../../../../props/finance/incomes/invoices/part';
import IncomeInvoicePartService from '../../../../../../../services/finance/incomes/invoices/part';
import LoadingComponent from '../../../../../../feedbacks/loading';
import Details from '../../../../../../typography/details';
import PricingService from '../../../../../../../services/general/pricing';

type IncomeInvoicePartFormProps = {
    invoiceId: string;
    partId?: string;
    onDismissed(refresh?: boolean): void;
}

const IncomeInvoicePartForm: React.FC<IncomeInvoicePartFormProps> = (props: IncomeInvoicePartFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [invoice, setInvoice] = React.useState<IIncomeInvoiceResourceShortProps | undefined>();
    const [data, setData] = React.useState<Partial<IIncomeInvoicePartResourceShortProps>>({
        id: GeneralService.guid(),
        dueDate: moment().add(1, 'week').toISOString()
    });
    const [remainingAmount, setRemainingAmount] = React.useState<number>(0);
    const [error, setError] = React.useState<any>({});

    const mode: 'create' | 'update' = props.partId ? 'update' : 'create';
    const hasPermission = props.partId ? PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions) : PermissionsService.hasPermission(['incomes.invoices.create'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            const _invoice = await IncomeInvoiceService.get(props.invoiceId);
            setInvoice(_invoice);

            let _remainingAmount = Number(_invoice.totalAmount);
            
            if (props.partId) {
                const _data = await IncomeInvoicePartService.get(props.invoiceId, props.partId);
                _remainingAmount += Number(_data.amount)
                setData({ ..._data });
            } else {
                let key = 'A';
                const lastKey = (_invoice.parts.map((p) => p.key.charCodeAt(0)).sort().reverse())[0];
                if (lastKey !== undefined) {
                    key = String.fromCharCode(lastKey + 1);
                }

                setData({ ...data, key });
            }

            _invoice.parts.forEach((part) => {
                _remainingAmount -= Number(part.amount);
            });

            setLoaded(true);
            setRemainingAmount(_remainingAmount);
        } catch (e) {
            banner.add({
                key: 'get_part_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get part details. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if ((data.amount || "").trim() === "" || (data.key || "").trim() === "") {
            return true;
        } else if (Number(data.amount || "0") === Number(invoice?.totalAmount || "0"))  {
            return true;
        } else if (Object.values(error).filter(Boolean).length > 0) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { amount, ppn, pph, key, notes, dueDate } = data;

            // create form data
            let fd = new FormData();
            fd.append('key', key || "");
            fd.append('amount', amount || "");
            fd.append('ppn', ppn || "");
            fd.append('pph', pph || "");
            fd.append('notes', notes || "");
            fd.append('dueDate', dueDate || "");

            let part;

            if (!props.partId) {
                await IncomeInvoicePartService.create(props.invoiceId, fd);
            } else {
                await IncomeInvoicePartService.update(props.invoiceId, props.partId, fd);
            }

            banner.add({
                key: 'create_income_payment_success',
                variant: 'success',
                icon: faCheck,
                text: `Part has been ${mode}ed`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const calculatePercentage = () => {
        let text = undefined;

        if (data.amount && data.amount !== "" && invoice) {
            const percentage = Number((Number(data.amount) / Number(invoice.totalAmount) * 100).toFixed(2))
            text = percentage > 0 ? "(" + GeneralService.getNumberWithSeparator(percentage) + "%)" : undefined;
        }

        return text;
    }

    const calculatePrice = (_data: Partial<IIncomeInvoicePartResourceShortProps>) => {
        const pricing = PricingService.order.calculate({
            price: _data.amount || "0",
            ppn: "",
            pph: "",
            includePPn: true,
            includePPh: true,
            ppnPercentage: invoice?.ppnPercentage || "0",
            pphPercentage: invoice?.pphPercentage || "0",
            finalPrice: _data.amount || "0",
            nettPrice: _data.amount || "0",
            invoicePrice: _data.amount || "0"
        }, 'lumpsum');

        return pricing;
    }

    return <Panel headerText={mode === 'create' ? 'Create Part' : 'Update Part'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={`${mode === 'create' ? 'Creating' : 'Updating'} part ...`} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <LoadingComponent label={'Retrieving form ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
            {loaded ? <>
                <Stack.Item>
                    <TextField label={"Part Number"}
                        value={props.invoiceId + "-" + (data.key || "")}
                        disabled={true} />
                </Stack.Item>
                <Stack.Item>
                    <TextField label={"Remaining amount"}
                        value={GeneralService.getNumberWithSeparator(remainingAmount)}
                        prefix={'Rp'}
                        disabled={true} />
                </Stack.Item>
                <Stack.Item>
                    <TextField label={"Notes"}
                        value={data.notes}
                        multiline
                        rows={3}
                        resizable={false}
                        autoAdjustHeight
                        onChange={(evt, value) => {
                            data.notes = value || "";

                            const validation = ValidationService.combination(value, ['limit'], { maxChars: 2000 });
                            error.notes = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.notes}
                        disabled={submitting} />
                </Stack.Item>
                <Stack className={'divider'} />
                <Stack.Item>
                    <DatePicker label={"Due Date"}
                        isRequired={true}
                        value={moment(data.dueDate).toDate()}
                        onSelectDate={(date) => {
                            data.dueDate = date ? moment(date).toISOString() : data.dueDate;
                            setData({ ...data });
                        }}
                        formatDate={GeneralService.formatDate}
                        disabled={submitting} />
                </Stack.Item>
                <Stack.Item>
                    <TextField label={"Total amount (incl. PPn & PPh)"}
                        value={data.amount}
                        required={true}
                        prefix={'Rp'}
                        suffix={calculatePercentage()}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.amount = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 20, max: remainingAmount });
                                error.amount = validation.message;

                                if ((error.amount || "") === "" && Number(data.amount || "0") === Number(invoice?.totalAmount || "0")) {
                                    error.amount = "Invoice part amount cannot be the same with actual invoice amount.";
                                }

                                const {ppn, pph} = calculatePrice(data);
                                data.ppn = ppn;
                                data.pph = pph;

                                setData({ ...data });
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.amount}
                        disabled={submitting} />
                </Stack.Item>
                <Stack className={'divider'} />
                <Stack horizontal tokens={{childrenGap: 20}}>
                    <Details title={'PPn'} text={"Rp. " + GeneralService.getNumberWithSeparator(data.ppn || "0")} style={{width: '33%'}} />
                    <Details title={'PPh'} text={"Rp. " + GeneralService.getNumberWithSeparator(data.pph || "0")} style={{width: '33%'}} />
                    <Details title={'Invoice price'} text={"Rp. " + (GeneralService.getNumberWithSeparator(Number(data.amount || '0') - Number(data.pph || '0')))} style={{width: '33%'}} />
                </Stack>
            </> : null}
        </Stack>
    </Panel>
};

export default IncomeInvoicePartForm;
